'use client';

import { SvgBookmarkFill, SvgChatbubbleTypingFill } from '@common/icon';
import { cn } from '@core/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { throttle } from 'es-toolkit';
import { useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';
import { Transition } from 'react-transition-group';

import {
  AuthQueryOptions,
  BOOKMARK_TYPE,
  COMMENT_TYPE,
  UnauthQueryOptions,
} from '@/api';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { CustomLink } from '@/app/_components/CustomLink';
import type { CommentListWithFieldProps } from '@/components/template/CommentListWithField';
import { CommentListWithField } from '@/components/template/CommentListWithField';
import { ROUTES, SELECTOR } from '@/constants';
import { useBookmark } from '@/hooks/useBookmark';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';

type UserActionNavigationProps = Pick<
  CommentListWithFieldProps,
  'contentId' | 'initialData' | 'title'
> & {
  contentId: number;
  title: string;
  policyAgency?: string;
};

export const UserActionSection = ({
  contentId,
  initialData,
  title,
  policyAgency,
}: UserActionNavigationProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);

  const { data } = useQuery({
    ...(isLogin
      ? AuthQueryOptions['get:/wello/v2/auth/policy/feedback/{policyId}']
      : UnauthQueryOptions['get:/wello/v2/allows/policy/feedback/{policyId}'])(
      {
        path: {
          policyId: contentId,
        },
      },
    ),
    enabled: isLogin !== undefined,
    placeholderData: keepPreviousData,
    select: ({ context }) => ({
      readingCount: context?.readingCount ?? 0,
      hasNewComment: context?.newCommentYN ?? false,
      isThumbUp: context?.likeYN ?? false,
      thumbUpCount: context?.likeCount ?? 0,
      isBookmarked: context?.wishlistYN,
      bookmarkCount: context?.wishlistCount ?? 0,
      commentCount: context?.commentCount ?? 0,
      likeCount: context?.likeCount ?? 0,
    }),
  });

  const { bookmark } = useBookmark();

  const router = useCustomRouter();

  const handleUserAction = (callback: () => void) => () => {
    if (isLogin) {
      return callback();
    }

    const url = new URL(window.location.href);

    router.push(
      ROUTES.LOGIN.withSearchParams({
        searchParams: {
          redirect: `${url.pathname}${url.search}`,
        },
      }),
    );
  };

  const [isCommentFieldDisplay, setIsCommentFieldDisplay] = useState(false);
  const transitionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mainElement = document.getElementById(SELECTOR.MAIN);

    if (mainElement) {
      const handleCommentFieldDisplay = (el: HTMLElement) => {
        const { scrollTop, clientHeight, scrollHeight } = el;

        setIsCommentFieldDisplay((prevValue) => {
          return (
            scrollHeight <= scrollTop + clientHeight + (prevValue ? 81 : 54)
          );
        });
      };

      const handleScroll = throttle((e: Event) => {
        if (!e.target) return;

        const target = e.target as HTMLElement;

        handleCommentFieldDisplay(target);
      }, 100);

      mainElement.addEventListener('scroll', handleScroll);

      return () => {
        mainElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  if (!data)
    return (
      <CommentListWithField
        className="invisible max-h-100 overflow-hidden"
        commentFieldClassName="animate-fade-up animate-duration-75"
        commentType={COMMENT_TYPE.POLICY}
        title={title}
        policyName={title}
        contentId={contentId}
        initialData={initialData}
      />
    );

  return (
    <div className="mt-24 flex flex-1 flex-col justify-between border-t-4 border-Gray-200">
      <Transition in={isCommentFieldDisplay} nodeRef={transitionRef} timeout={150}>
        {(state) => {
          return (
            <div ref={transitionRef}>
              <CommentListWithField
                commentFieldClassName="animate-fade-up animate-duration-75"
                commentType={COMMENT_TYPE.POLICY}
                contentId={contentId}
                title={title}
                policyName={title}
                hasCommentField={state === 'entered'}
                initialData={initialData}
              />
              <BottomNavPortal>
                <nav
                  className={cn(
                    'flex h-54 w-full items-center justify-end gap-12 px-20 shadow-lg animate-duration-150',
                    {
                      hidden: state === 'entered',
                      'animate-fade-up animate-reverse': state === 'entering',
                      'animate-fade-up': state === 'exiting',
                    },
                  )}
                >
                  <CustomLink
                    className="group flex items-center gap-4"
                    href={ROUTES.COMMENT_LIST.pathname({
                      commentType: COMMENT_TYPE.POLICY,
                      commentId: 0,
                      contentId,
                    })}
                    title="댓글"
                  >
                    <div className="relative">
                      <SvgChatbubbleTypingFill className="size-18 text-Gray-400 transition-all duration-75 group-active:scale-75" />
                      {data.hasNewComment ? (
                        <div className="absolute -right-1 -top-1 size-9 rounded-pill bg-Gray-white flex-center">
                          <div className="size-7 rounded-pill bg-R-500" />
                        </div>
                      ) : null}
                    </div>
                    <CountUp
                      preserveValue
                      className="font-500 text-Gray-800 typo-body4"
                      duration={1}
                      end={data.commentCount}
                    />
                  </CustomLink>
                  <button
                    aria-label="스크랩"
                    className="group flex items-center gap-4"
                    title="스크랩"
                    type="button"
                    onClick={handleUserAction(() => {
                      bookmark({
                        contentId,
                        isBookmarked: data.isBookmarked ?? false,
                        bookmarkType: BOOKMARK_TYPE.POLICY,
                        contentName: title,
                        policyAgency,
                      });
                    })}
                  >
                    <SvgBookmarkFill
                      className={cn(
                        'size-20 text-Gray-400 transition-all duration-75 group-active:scale-75',
                        data.isBookmarked && 'text-B-500',
                      )}
                    />
                    <CountUp
                      preserveValue
                      className="font-500 text-Gray-800 typo-body4"
                      duration={1}
                      end={data.bookmarkCount}
                    />
                  </button>
                </nav>
              </BottomNavPortal>
            </div>
          );
        }}
      </Transition>
    </div>
  );
};
